import { useCallback, useEffect, useRef, useState } from 'react';
import {
  ApiError,
  assembleDocument,
  createDraft,
  deleteDocument,
  getDocument,
  listDrafts,
  listPages,
  updateMetadata,
  MetadataPayload,
} from './api';
import { DraftDocument } from './types';
import { errorText, usePages } from './usePages';

// The lifecycle of a draft in the wizard (ROADMAP.md, Part 3,
// "option A"): the document exists on the server from the start, and each
// page is uploaded and validated against it instantly.
//
//   checking  on open: are there own drafts?
//   choose    there are: it offers to resume the most recent or start another
//   loading   creating the new draft, or loading the resumed one
//   ready     document and pages in hand
//   error     none of the above was possible
//
// The pages (list, upload queue, deletion, reordering) are usePages,
// which the editing of an archived document also uses; here goes only what is
// specific to the draft: choosing it, creating it, describing it, assembling it
// or discarding it.

type Phase =
  | { name: 'checking' }
  | { name: 'choose'; drafts: DraftDocument[] }
  | { name: 'loading' }
  | { name: 'ready' }
  | { name: 'error'; message: string };

export const useDraft = (userId: string | undefined, enabled: boolean) => {
  const [phase, setPhase] = useState<Phase>({ name: 'checking' });
  const [document, setDocument] = useState<DraftDocument | null>(null);

  const documentRef = useRef<DraftDocument | null>(null);
  const startedRef = useRef(false);

  documentRef.current = document;

  const pages = usePages(document?.id);
  const { reset: resetPages, cancelUploads } = pages;

  // --- Startup ----------------------------------------------------------

  const startNew = useCallback(async () => {
    setPhase({ name: 'loading' });
    try {
      const created = await createDraft();
      setDocument(created);
      resetPages([]);
      setPhase({ name: 'ready' });
    } catch (err) {
      setPhase({ name: 'error', message: errorText(err) });
    }
  }, [resetPages]);

  const resume = useCallback(
    async (draft: DraftDocument) => {
      setPhase({ name: 'loading' });
      try {
        const [loaded, fromServer] = await Promise.all([
          getDocument(draft.id),
          listPages(draft.id),
        ]);
        setDocument(loaded);
        resetPages(fromServer);
        setPhase({ name: 'ready' });
      } catch (err) {
        setPhase({ name: 'error', message: errorText(err) });
      }
    },
    [resetPages],
  );

  useEffect(() => {
    if (!enabled || !userId || startedRef.current) return;
    // Once per mount. StrictMode runs the effect and cleanup twice
    // in development: the cleanup cancels the first pass and releases the
    // guard so that the second starts; without the guard two drafts would be
    // created, and without releasing it none would be created.
    startedRef.current = true;

    let cancelled = false;

    listDrafts(userId)
      .then((drafts) => {
        if (cancelled) return;
        if (drafts.length > 0) {
          setPhase({ name: 'choose', drafts });
        } else {
          startNew();
        }
      })
      .catch(() => {
        // Without a draft list it is fine to continue: a new one is created.
        if (!cancelled) startNew();
      });

    return () => {
      cancelled = true;
      startedRef.current = false;
    };
  }, [enabled, userId, startNew]);

  // --- Metadata, assembly, discard --------------------------------------

  const saveMetadata = useCallback(
    async (payload: Omit<MetadataPayload, 'id'>) => {
      const current = documentRef.current;
      if (!current) throw new ApiError(0);
      await updateMetadata({ id: current.id, ...payload });
      setDocument({
        ...current,
        title: payload.title,
        issuing_date: payload.issuing_date,
        service: payload.service,
        issuer: payload.issuer,
        confidential: payload.confidential,
        verification_url: payload.verification_url,
      });
    },
    [],
  );

  const assemble = useCallback(async () => {
    const current = documentRef.current;
    if (!current) throw new ApiError(0);
    return assembleDocument(current.id);
  }, []);

  const discard = useCallback(async () => {
    const current = documentRef.current;
    cancelUploads();
    if (!current) return;
    await deleteDocument(current.id);
    setDocument(null);
    resetPages([]);
  }, [cancelUploads, resetPages]);

  return {
    phase,
    document,
    rows: pages.rows,
    pages: pages.pages,
    qr: pages.qr,
    uploadsInProgress: pages.uploadsInProgress,
    busy: pages.busy,
    notice: pages.notice,
    clearNotice: pages.clearNotice,
    startNew,
    resume,
    addFiles: pages.addFiles,
    retryUpload: pages.retryUpload,
    dismissUpload: pages.dismissUpload,
    removePage: pages.removePage,
    movePage: pages.movePage,
    reloadPages: pages.reloadPages,
    saveMetadata,
    assemble,
    discard,
  };
};

export type DraftController = ReturnType<typeof useDraft>;
